import Link from 'next/link'
import Head from 'next/head'
import Seo, { SITE_URL } from '@comp/seo'
import Layout from '@comp/layout'
import FinalCta from '@comp/home/final-cta'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'
import {
  glossaryTerms,
  glossaryCategories,
  pick,
  GlossaryTerm,
} from '@lib/glossary'

const ui = {
  eyebrow: { en: 'Glossary', tr: 'Sözlük' },
  title: {
    en: 'The identity security glossary.',
    tr: 'Kimlik güvenliği sözlüğü.',
  },
  lede: {
    en: 'Plain-language definitions for the terms behind IAM, PAM, and identity governance, written by the team that builds them.',
    tr: 'IAM, PAM ve kimlik yönetişiminin arkasındaki kavramların sade tanımları, bu ürünleri geliştiren ekipten.',
  },
  seoTitle: {
    en: 'Identity Security Glossary | Monofor',
    tr: 'Kimlik Güvenliği Sözlüğü | Monofor',
  },
  seoDescription: {
    en: 'What is PAM? What is SSO? Plain-language definitions of identity and access management, privileged access, and governance terms.',
    tr: "PAM nedir? SSO nedir? Kimlik ve erişim yönetimi, ayrıcalıklı erişim ve yönetişim kavramlarının sade tanımları.",
  },
}

type Props = {
  locale: string
}

export default function GlossaryIndex({ locale }: Props) {
  const basePath = locale === 'tr' ? '/tr' : ''
  return (
    <Layout>
      <Seo
        title={pick(ui.seoTitle, locale)}
        description={pick(ui.seoDescription, locale)}
        path={`${basePath}/glossary`}
      />
      <Head>
        <link rel="alternate" hrefLang="en" href={`${SITE_URL}/glossary`} />
        <link rel="alternate" hrefLang="tr" href={`${SITE_URL}/tr/glossary`} />
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/glossary`} />
      </Head>
      <main className="bg-surface text-ink-primary">
        <Section tone="default" density="compact" className="relative overflow-hidden">
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div className="absolute -top-32 -right-24 h-[640px] w-[640px] rounded-full bg-brand-100/60 blur-3xl" />
            <div className="absolute top-40 -left-32 h-[420px] w-[420px] rounded-full bg-accent-100/50 blur-3xl" />
          </div>
          <Container size="xl" className="relative">
            <div className="max-w-3xl">
              <Eyebrow tone="brand">{pick(ui.eyebrow, locale)}</Eyebrow>
              <div className="mt-6">
                <Heading as="h1" size="display-xl">
                  {pick(ui.title, locale)}
                </Heading>
              </div>
              <div className="mt-8 max-w-xl">
                <Text size="xl" tone="secondary">
                  {pick(ui.lede, locale)}
                </Text>
              </div>
            </div>
          </Container>
        </Section>

        <Section tone="subtle" density="default">
          <Container size="xl">
            {glossaryCategories.map((cat) => {
              const terms = glossaryTerms.filter((t) => t.category === cat.key)
              if (!terms.length) return null
              return (
                <div key={cat.key} className="mb-16 last:mb-0">
                  <Heading as="h2" size="display-sm">
                    {pick(cat.label, locale)}
                  </Heading>
                  <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {terms.map((term: GlossaryTerm) => (
                      <Link
                        key={term.slug}
                        href={`/glossary/${term.slug}`}
                        className="group flex h-full flex-col rounded-2xl border border-line-subtle bg-surface p-6 shadow-card transition duration-300 ease-smooth hover:-translate-y-0.5 hover:border-line hover:shadow-card-hover"
                      >
                        <span className="text-body-lg font-medium text-ink-primary group-hover:text-brand-800">
                          {pick(term.term, locale)}
                        </span>
                        <span className="mt-2 flex-grow text-body-sm text-ink-tertiary line-clamp-3">
                          {pick(term.short, locale)}
                        </span>
                        <span className="mt-4 inline-flex items-center gap-1.5 text-body-sm font-medium text-brand-700 transition-transform group-hover:translate-x-0.5">
                          {locale === 'tr' ? 'Devamını oku' : 'Read more'}
                          <span aria-hidden>→</span>
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )
            })}
          </Container>
        </Section>

        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
